import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiBookOpen,
  FiChevronDown,
  FiClock,
  FiCode,
  FiDownload,
  FiFileText,
  FiBriefcase,
  FiGrid,
  FiHeart,
  FiLayers,
  FiMap,
  FiRepeat,
  FiSearch,
  FiShield,
  FiStar,
  FiX,
  FiZap,
} from "react-icons/fi";
import "./EducationalAreaPage.css";

// ─────────────────────────────────────────────
// Data
// ─────────────────────────────────────────────

const CATEGORIES = [
  "Todos",
  "Cultura",
  "Institucional",
  "Crédito",
  "Operações",
  "Estratégia",
  "Vendas",
];

const SECTIONS = [
  {
    id: "onboarding",
    name: "Onboarding — Boas-vindas",
    description:
      "Seu ponto de partida na Amplia Capital. Conheça a empresa, a equipe e tudo o que você precisa para começar com confiança.",
    Icon: FiZap,
    color: "dark",
    category: "Cultura",
    level: "Fundamental",
    levelTone: "green",
    estimatedMin: 20,
    featured: true,
  },
  {
    id: "apresentacao",
    name: "Apresentação Institucional",
    description:
      "Visão geral da Amplia Capital: história, missão, valores e posicionamento estratégico no mercado de crédito.",
    Icon: FiStar,
    color: "blue",
    category: "Institucional",
    level: "Fundamental",
    levelTone: "green",
    estimatedMin: 15,
  },
  {
    id: "deck-cultural",
    name: "Deck Cultural",
    description:
      "Valores, princípios e a cultura que guiam cada decisão e comportamento dentro da Amplia Capital.",
    Icon: FiHeart,
    color: "rose",
    category: "Cultura",
    level: "Fundamental",
    levelTone: "green",
    estimatedMin: 12,
  },
  {
    id: "areas-negocio",
    name: "Áreas do Negócio",
    description:
      "Mapeamento dos departamentos, responsabilidades e como cada área se interliga para gerar resultado.",
    Icon: FiGrid,
    color: "indigo",
    category: "Institucional",
    level: "Intermediário",
    levelTone: "blue",
    estimatedMin: 25,
  },
  {
    id: "plano-negocios",
    name: "Plano de Negócios",
    description:
      "Estratégia de crescimento, metas corporativas e o roteiro que nos guia rumo ao sucesso sustentável.",
    Icon: FiBriefcase,
    color: "emerald",
    category: "Estratégia",
    level: "Intermediário",
    levelTone: "blue",
    estimatedMin: 30,
  },
  {
    id: "dossie-credito",
    name: "Dossiê de Crédito",
    description:
      "Documentação completa sobre análise, concessão e gestão de crédito — base do nosso negócio.",
    Icon: FiFileText,
    color: "teal",
    category: "Crédito",
    level: "Avançado",
    levelTone: "purple",
    estimatedMin: 35,
  },
  {
    id: "politicas-credito",
    name: "Políticas de Crédito",
    description:
      "Regras, critérios e diretrizes que garantem consistência e qualidade na operação de crédito.",
    Icon: FiShield,
    color: "purple",
    category: "Crédito",
    level: "Avançado",
    levelTone: "purple",
    estimatedMin: 40,
  },
  {
    id: "mapeamento-operacoes",
    name: "Mapeamento de Operações",
    description:
      "Fluxos operacionais detalhados com responsáveis, etapas e indicadores de cada processo crítico.",
    Icon: FiMap,
    color: "cyan",
    category: "Operações",
    level: "Intermediário",
    levelTone: "blue",
    estimatedMin: 28,
  },
  {
    id: "rotinas",
    name: "Rotinas",
    description:
      "Rotinas diárias, semanais e mensais que garantem a eficiência e consistência operacional da equipe.",
    Icon: FiRepeat,
    color: "emerald",
    category: "Operações",
    level: "Fundamental",
    levelTone: "green",
    estimatedMin: 18,
  },
  {
    id: "scripts",
    name: "Scripts de Atendimento",
    description:
      "Guias de abordagem, argumentação e scripts testados para diferentes cenários de atendimento.",
    Icon: FiCode,
    color: "violet",
    category: "Vendas",
    level: "Intermediário",
    levelTone: "blue",
    estimatedMin: 22,
  },
];

const EXISTING_DOCS_BY_SECTION = {
  onboarding: ["Bem-vindo-ao-Time-Amplia-Capital.pptx"],
  apresentacao: [
    "Amplia-Capital - apresentação curta.pptx",
    "Amplia-Capital-Apresentacao-pdf (1).pptx",
    "Inteligencia-em-Credito-Empresarial (1).pptx",
    "apresentação melhorada.pptx",
  ],
  "deck-cultural": ["Deck-Cultural-Amplia-Capital.pptx"],
  "areas-negocio": ["Areas-do-Negocio-or-Amplia-Capital.pptx"],
  "plano-negocios": [],
  "dossie-credito": ["Dossie-e-Checklist-de-Credito-Empresarial.pptx"],
  "politicas-credito": [],
  "mapeamento-operacoes": [
    "Amplia-Capitalpptx.pptx",
    "Mapeamento-de-Operacoes-de-Credito (1).pptx",
    "Mapeamento-de-Operacoes-de-Credito - versão melhorada.pptx",
  ],
  rotinas: [
    "A-Engrenagem-Amplia-Capital (1).pptx",
    "Gestao-de-Rotina-Amplia-Capital.pptx",
  ],
  scripts: [],
};

const getTypeFromFileName = (fileName) => {
  const ext = fileName.split(".").pop()?.toLowerCase() || "";
  if (ext === "ppt" || ext === "pptx") return "PPT";
  if (ext === "pdf") return "PDF";
  if (ext === "doc" || ext === "docx") return "DOC";
  return ext.toUpperCase() || "ARQ";
};

const formatDisplayName = (fileName) =>
  fileName
    .replace(/\.[^/.]+$/, "")
    .replace(/\(\d+\)/g, "")
    .replace(/[-_]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

const buildFileUrl = (sectionId, fileName) => {
  const encodedSection = encodeURIComponent(sectionId);
  const encodedFile = encodeURIComponent(fileName);
  return `/assets/educational/${encodedSection}/${encodedFile}`;
};

const sectionsWithDocs = SECTIONS.map((s) => {
  const files = EXISTING_DOCS_BY_SECTION[s.id] || [];
  return {
    ...s,
    docs: files.map((fileName, i) => ({
      id: `${s.id}-${i}`,
      fileName,
      name: formatDisplayName(fileName),
      type: getTypeFromFileName(fileName),
      size: "Arquivo local",
      updatedAt: "disponivel",
      url: buildFileUrl(s.id, fileName),
    })),
  };
});

// ─────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────

export default function EducationalAreaPage() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("Todos");
  const [expandedId, setExpandedId] = useState(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return sectionsWithDocs.filter((s) => {
      if (category !== "Todos" && s.category !== category) return false;
      if (!term) return true;
      return (
        s.name.toLowerCase().includes(term) ||
        s.description.toLowerCase().includes(term) ||
        s.docs.some((d) => d.name.toLowerCase().includes(term))
      );
    });
  }, [search, category]);

  const featured = filtered.find((s) => s.featured);
  const grid = filtered.filter((s) => !s.featured);

  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const totalDocs = sectionsWithDocs.reduce((acc, s) => acc + s.docs.length, 0);
  const totalMin = sectionsWithDocs.reduce((acc, s) => acc + s.estimatedMin, 0);

  return (
    <section className="edu-page">
      {/* ── Hint ── */}
      {(search.trim() || category !== "Todos") && (
        <p className="edu-hint">
          {filtered.length} trilha{filtered.length !== 1 ? "s" : ""}
          {category !== "Todos" && ` · ${category}`}
          {search.trim() && ` · "${search.trim()}"`}
        </p>
      )}

      {/* ── Featured ── */}
      {featured && (
        <FeaturedCard
          section={featured}
          expanded={expandedId === featured.id}
          onToggle={() => toggle(featured.id)}
        />
      )}

      {/* ── Filters ── */}
      <div
        className="edu-filters"
        role="group"
        aria-label="Filtrar por categoria"
      >
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            className={`edu-chip${category === cat ? " edu-chip--on" : ""}`}
            onClick={() => setCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* ── Grid header ── */}
      {grid.length > 0 && (
        <>
          {featured && (
            <div className="edu-section-header">
              <span className="edu-section-label">Todas as trilhas</span>
              <span className="edu-section-count">{grid.length}</span>
            </div>
          )}

          <div className="edu-grid">
            {grid.map((section, i) => (
              <ModuleCard
                key={section.id}
                section={section}
                index={i}
                expanded={expandedId === section.id}
                onToggle={() => toggle(section.id)}
              />
            ))}
          </div>
        </>
      )}

      {/* ── Empty ── */}
      {filtered.length === 0 && (
        <div className="edu-empty">
          <FiBookOpen size={36} className="edu-empty-icon" />
          <p className="edu-empty-title">Nenhuma trilha encontrada</p>
          <p className="edu-empty-sub">
            Tente ajustar o filtro ou o termo de busca.
          </p>
          <button
            type="button"
            className="edu-empty-btn"
            onClick={() => {
              setSearch("");
              setCategory("Todos");
            }}
          >
            Limpar filtros
          </button>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────
// Featured card
// ─────────────────────────────────────────────

function FeaturedCard({ section, expanded, onToggle }) {
  const {
    Icon,
    color,
    category,
    level,
    levelTone,
    estimatedMin,
    name,
    description,
    docs,
  } = section;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28 }}
      className={`edu-featured edu-featured--${color}`}
    >
      <div className="edu-featured-top">
        <div className="edu-featured-icon-wrap">
          <Icon size={26} />
        </div>
        <div className="edu-featured-info">
          <div className="edu-badges">
            <span className="edu-badge edu-badge--start">⭐ Comece aqui</span>
            <span className="edu-badge edu-badge--cat">{category}</span>
            <LevelBadge level={level} tone={levelTone} />
          </div>
          <h2 className="edu-featured-name">{name}</h2>
          <p className="edu-featured-desc">{description}</p>
        </div>
      </div>

      <button
        type="button"
        className={`edu-expand-btn${expanded ? " edu-expand-btn--on" : ""}`}
        onClick={onToggle}
      >
        <span>{expanded ? "Ocultar conteúdo" : "Ver conteúdo"}</span>
        <motion.span
          className="edu-expand-chevron"
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.18 }}
        >
          <FiChevronDown size={15} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="featured-docs"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="edu-doclist">
              {docs.map((doc) => (
                <DocRow key={doc.id} doc={doc} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─────────────────────────────────────────────
// Module card
// ─────────────────────────────────────────────

function ModuleCard({ section, index, expanded, onToggle }) {
  const {
    Icon,
    color,
    category,
    level,
    levelTone,
    estimatedMin,
    name,
    description,
    docs,
  } = section;
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, delay: index * 0.04 }}
      className={`edu-card edu-card--${color}`}
    >
      <div className="edu-card-row">
        <div className="edu-card-icon">
          <Icon size={17} />
        </div>
        <div className="edu-card-content">
          <h3 className="edu-card-name">{name}</h3>
          <p className="edu-card-desc">{description}</p>
          <div className="edu-badges">
            <LevelBadge level={level} tone={levelTone} />
            <span className="edu-badge edu-badge--cat">{category}</span>
          </div>
        </div>
      </div>

      <button
        type="button"
        className={`edu-expand-btn${expanded ? " edu-expand-btn--on" : ""}`}
        onClick={onToggle}
      >
        <span>{expanded ? "Ocultar conteúdo" : "Ver conteúdo"}</span>
        <motion.span
          className="edu-expand-chevron"
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.18 }}
        >
          <FiChevronDown size={15} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="card-docs"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="edu-doclist edu-doclist--card">
              {docs.map((doc) => (
                <DocRow key={doc.id} doc={doc} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}

// ─────────────────────────────────────────────
// Shared atoms
// ─────────────────────────────────────────────

function DocRow({ doc }) {
  return (
    <a
      href={doc.url}
      target="_blank"
      rel="noreferrer"
      download
      className="edu-doc-row"
    >
      <span className="edu-doc-text">
        <span className="edu-doc-name">{doc.name}</span>
        <span className="edu-doc-meta">
          {doc.size} · Atualizado {doc.updatedAt}
        </span>
        <span
          className={`edu-doc-badge edu-doc-badge--${doc.type.toLowerCase()} w-fit`}
        >
          {doc.type}
        </span>
      </span>
      <FiDownload size={13} className="edu-doc-dl" />
    </a>
  );
}

function LevelBadge({ level, tone }) {
  return (
    <span className={`edu-badge edu-badge--level edu-badge--${tone}`}>
      {level}
    </span>
  );
}
